// src/pages/kitchen/KitchenManagerDashboard.tsx
import React, { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUserStore } from "../../store/user.store";
import { db } from "../../lib/firebase";
import { call } from "../../services/callables";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { Spinner } from "../../components/common/Spinner";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { toast } from "react-hot-toast";
import { Kitchen } from "../../types/index";
import { getKitchensForManager } from "../../services/kitchen.service";
import { KitchenOrderManager } from "../../components/kitchen/KitchenOrderManager";
import ManageMenu from "../chef/ManageMenu";

const ManageSpecials = lazy(() => import("../chef/ManageSpecials"));
const PasteAnnouncement = lazy(() => import("../../components/announce/PasteAnnouncement"));

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "orders", label: "Orders" },
  { key: "announce", label: "📋 Paste" },
  { key: "menu", label: "Menu" },
  { key: "specials", label: "Specials" },
  { key: "chefs", label: "Chefs" },
  { key: "money", label: "Money" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const isKitchenOnline = (status?: string) => {
  const s = (status || "").toLowerCase();
  return s === "open" || s === "active";
};

const kitchenDocId = (k: Kitchen) => (k.id || k.uid || "") as string;

type ChefUser = {
  uid: string;
  name?: string;
  displayName?: string;
  phone?: string;
  roles?: string[];
};

const KitchenChefsPanel: React.FC<{ kitchen: Kitchen }> = ({ kitchen }) => {
  const queryClient = useQueryClient();
  const kitchenId = kitchenDocId(kitchen);
  const [phoneSearch, setPhoneSearch] = useState("");
  const [selectedToAdd, setSelectedToAdd] = useState<string[]>([]);

  const chefUids: string[] = useMemo(() => {
    const list = Array.isArray(kitchen.chefUids)
      ? [...kitchen.chefUids]
      : (kitchen as any).chefUid
      ? [(kitchen as any).chefUid]
      : [];
    return Array.from(new Set(list.filter(Boolean)));
  }, [kitchen]);

  const { data: linkedChefs = [], isLoading: loadingLinked } = useQuery(
    ["kitchenLinkedChefs", kitchenId, chefUids.join(",")],
    async () => {
      const results: ChefUser[] = [];
      for (const uid of chefUids) {
        const snap = await getDoc(doc(db, "users", uid));
        if (snap.exists()) {
          results.push({ uid: snap.id, ...(snap.data() as any) });
        } else {
          results.push({ uid, name: "(missing user)" });
        }
      }
      return results;
    },
    { enabled: !!kitchenId }
  );

  const { data: allChefs = [], isLoading: loadingAll } = useQuery(
    ["allChefUsers"],
    async () => {
      // users/* bulk reads are restricted by rules; the server lists chef profiles for managers.
      const res = await call<{ chefs: ChefUser[] }>("listChefUsers", {});
      return (res?.chefs || []).filter((u: any) => u.roles?.includes("chef")) as ChefUser[];
    }
  );

  const availableChefs = useMemo(() => {
    const linked = new Set(chefUids);
    let list = allChefs.filter((c) => !linked.has(c.uid));
    const q = phoneSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (c) =>
          (c.phone || "").toLowerCase().includes(q) ||
          (c.name || "").toLowerCase().includes(q) ||
          (c.displayName || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [allChefs, chefUids, phoneSearch]);

  const persistChefUids = async (next: string[]) => {
    const unique = Array.from(new Set(next.filter(Boolean)));
    await updateDoc(doc(db, "kitchens", kitchenId), {
      chefUids: unique,
      chefUid: unique[0] || null,
    });
    queryClient.invalidateQueries(["managerKitchens"]);
    queryClient.invalidateQueries(["kitchenLinkedChefs", kitchenId]);
  };

  const { mutate: removeChef, isLoading: removing } = useMutation(
    async (uid: string) => {
      await persistChefUids(chefUids.filter((id) => id !== uid));
    },
    {
      onSuccess: () => toast.success("Chef removed"),
      onError: () => toast.error("Failed to remove chef"),
    }
  );

  const { mutate: addChefs, isLoading: adding } = useMutation(
    async (uids: string[]) => {
      await persistChefUids([...chefUids, ...uids]);
    },
    {
      onSuccess: () => {
        toast.success("Chef(s) added");
        setSelectedToAdd([]);
        setPhoneSearch("");
      },
      onError: () => toast.error("Failed to add chef"),
    }
  );

  const toggleSelect = (uid: string) => {
    setSelectedToAdd((prev) =>
      prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]
    );
  };

  if (loadingLinked || loadingAll) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h3 className="font-bold text-gray-900 mb-3">Linked chefs</h3>
        {linkedChefs.length === 0 ? (
          <p className="text-sm text-gray-500">No chefs linked yet.</p>
        ) : (
          <ul className="space-y-2">
            {linkedChefs.map((c) => (
              <li
                key={c.uid}
                className="flex items-center justify-between gap-2 border border-gray-100 rounded-xl px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">
                    {c.name || c.displayName || c.uid}
                    {chefUids[0] === c.uid ? (
                      <span className="ml-2 text-[10px] uppercase text-indigo-600 font-bold">
                        primary
                      </span>
                    ) : null}
                  </p>
                  <p className="text-xs text-gray-500">{c.phone || "—"}</p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="text-red-600 border-red-100 min-h-[40px]"
                  disabled={removing}
                  onClick={() => {
                    if (window.confirm(`Remove ${c.name || c.phone || c.uid}?`)) {
                      removeChef(c.uid);
                    }
                  }}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="font-bold text-gray-900 mb-2">Add chefs</h3>
        <input
          type="search"
          placeholder="Search by phone or name"
          value={phoneSearch}
          onChange={(e) => setPhoneSearch(e.target.value)}
          className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm mb-3 min-h-[44px]"
        />
        <div className="max-h-56 overflow-y-auto space-y-1 mb-3">
          {availableChefs.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">
              No matching chef users
            </p>
          ) : (
            availableChefs.slice(0, 40).map((c) => (
              <label
                key={c.uid}
                className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedToAdd.includes(c.uid)}
                  onChange={() => toggleSelect(c.uid)}
                  className="rounded text-orange-600"
                />
                <span className="text-sm min-w-0">
                  <span className="font-semibold block truncate">
                    {c.name || c.displayName || "Chef"}
                  </span>
                  <span className="text-xs text-gray-500">{c.phone}</span>
                </span>
              </label>
            ))
          )}
        </div>
        <Button
          type="button"
          disabled={!selectedToAdd.length || adding}
          isLoading={adding}
          onClick={() => addChefs(selectedToAdd)}
          className="w-full min-h-[44px] bg-orange-600 hover:bg-orange-700"
        >
          Add selected ({selectedToAdd.length})
        </Button>
      </Card>
    </div>
  );
};

const KitchenMoneyPanel: React.FC<{ kitchenId: string }> = ({ kitchenId }) => {
  const { data: wallet, isLoading } = useQuery(
    ["kitchenWallet", kitchenId],
    async () => {
      const direct = await getDoc(doc(db, "wallets", kitchenId));
      if (direct.exists()) {
        return { id: direct.id, ...direct.data() } as any;
      }
      // Legacy wallets keyed by another id: only readable if rules allow (admin); ignore denial.
      try {
        const q = query(
          collection(db, "wallets"),
          where("ownerId", "==", kitchenId)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0];
          return { id: d.id, ...d.data() } as any;
        }
      } catch (e) {
        console.warn("Kitchen wallet lookup by ownerId not permitted", e);
      }
      return null;
    },
    { enabled: !!kitchenId }
  );

  if (isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  const balance = Number(wallet?.balance ?? 0);
  const due = Number(wallet?.dueBalance ?? wallet?.due ?? 0);

  return (
    <div className="space-y-4">
      <Card className="p-5 border-l-4 border-emerald-500">
        <p className="text-xs font-bold uppercase text-gray-400 mb-1">
          Kitchen wallet
        </p>
        {wallet ? (
          <>
            <p className="text-3xl font-black text-gray-900">
              ₹{balance.toFixed(2)}
            </p>
            {due > 0 && (
              <p className="text-sm text-amber-700 mt-1">
                Due / pending payout: ₹{due.toFixed(2)}
              </p>
            )}
            <p className="text-xs text-gray-400 mt-2 font-mono">
              wallet id: {wallet.id}
            </p>
          </>
        ) : (
          <p className="text-gray-500 text-sm">
            No wallet found for this kitchen yet. Earnings appear here after
            settled orders.
          </p>
        )}
      </Card>
      <Card className="p-4 bg-slate-50">
        <p className="text-sm text-gray-600">
          Payouts are processed by Ruchira admin. There is no self-serve cashout
          in this dashboard — contact support / admin when you need a transfer.
        </p>
      </Card>
    </div>
  );
};

const KitchenManagerDashboard: React.FC = () => {
  const { userProfile } = useUserStore();
  const uid = userProfile?.uid;
  const isAdmin =
    userProfile?.currentRole === "admin" ||
    userProfile?.roles?.includes("admin");
  const queryClient = useQueryClient();

  const [selectedKitchenId, setSelectedKitchenId] = useState<string | null>(
    null
  );
  const [selectedTab, setSelectedTab] = useState<TabKey>("overview");

  const { data: kitchens = [], isLoading } = useQuery(
    ["managerKitchens", uid, isAdmin],
    () => getKitchensForManager(uid!, !!isAdmin),
    { enabled: !!uid }
  );

  const visibleKitchens = kitchens.filter((k) => {
    const s = String(k.status || "").toLowerCase();
    return s !== "blocked";
  });

  useEffect(() => {
    if (visibleKitchens.length > 0 && !selectedKitchenId) {
      setSelectedKitchenId(kitchenDocId(visibleKitchens[0]));
    }
  }, [visibleKitchens, selectedKitchenId]);

  const selectedKitchen = visibleKitchens.find(
    (k) => kitchenDocId(k) === selectedKitchenId
  );

  const online = isKitchenOnline(selectedKitchen?.status);
  const kitchenName =
    selectedKitchen?.kitchen_name ||
    (selectedKitchen as any)?.name ||
    selectedKitchen?.id ||
    "";

  const { mutate: updateStatus, isLoading: isUpdatingStatus } = useMutation(
    async ({
      kitchenId,
      nextOnline,
    }: {
      kitchenId: string;
      nextOnline: boolean;
    }) => {
      await updateDoc(doc(db, "kitchens", kitchenId), {
        status: nextOnline ? "active" : "paused",
      });
      return nextOnline;
    },
    {
      onSuccess: (nextOnline) => {
        toast.success(nextOnline ? "Kitchen is online" : "Kitchen paused");
        queryClient.invalidateQueries(["managerKitchens", uid]);
      },
      onError: () => toast.error("Could not update status"),
    }
  );

  const { mutate: toggleSelfDelivery, isLoading: togglingDelivery } =
    useMutation(
      async (next: boolean) => {
        if (!selectedKitchen) return;
        await updateDoc(doc(db, "kitchens", kitchenDocId(selectedKitchen)), {
          selfDelivery: next,
        });
      },
      {
        onSuccess: () => {
          toast.success("Self-delivery updated");
          queryClient.invalidateQueries(["managerKitchens", uid]);
        },
        onError: () => toast.error("Failed to update self-delivery"),
      }
    );

  if (isLoading) {
    return (
      <div className="flex justify-center p-20">
        <Spinner />
      </div>
    );
  }

  if (!visibleKitchens.length) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <p className="text-lg font-bold text-gray-800 mb-2">
          No kitchens to manage
        </p>
        <p className="text-sm text-gray-500">
          Ask admin to set you as owner or manager on a kitchen.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-4 sm:py-8 px-3 sm:px-4 pb-28">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          Kitchen manager
        </h1>
        {visibleKitchens.length > 1 && (
          <select
            className="border border-gray-200 p-3 rounded-xl bg-white shadow-sm text-sm font-semibold w-full sm:w-auto min-h-[44px]"
            value={selectedKitchenId || ""}
            onChange={(e) => setSelectedKitchenId(e.target.value)}
          >
            {visibleKitchens.map((k) => (
              <option key={kitchenDocId(k)} value={kitchenDocId(k)}>
                {k.kitchen_name || (k as any).name || k.id}
                {!isKitchenOnline(k.status) ? " (paused)" : ""}
              </option>
            ))}
          </select>
        )}
      </div>

      {selectedKitchen && (
        <>
          <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-xl overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setSelectedTab(tab.key)}
                className={`flex-1 min-w-[4.5rem] py-2.5 text-xs sm:text-sm font-bold rounded-lg transition-all min-h-[44px] ${
                  selectedTab === tab.key
                    ? "bg-white shadow text-orange-600"
                    : "text-gray-500"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <Suspense
            fallback={
              <div className="p-10 text-center">
                <Spinner />
              </div>
            }
          >
            {selectedTab === "overview" && (
              <Card className="p-4 sm:p-5 border-l-4 border-orange-500 space-y-4">
                <div>
                  <h2 className="font-bold text-lg sm:text-xl truncate">
                    {kitchenName}
                  </h2>
                  <p className="text-sm text-gray-500 break-words mt-1">
                    {selectedKitchen.address || "No address"}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-[10px] uppercase font-bold text-gray-400">
                      Kitchen phone
                    </p>
                    <p className="font-semibold">
                      {selectedKitchen.phone || "—"}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-[10px] uppercase font-bold text-gray-400">
                      Owner phone
                    </p>
                    <p className="font-semibold">
                      {(selectedKitchen as any).ownerPhone ||
                        (selectedKitchen as any).owner_phone ||
                        "—"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100">
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase">
                      Online status
                    </p>
                    <span
                      className={`text-sm font-bold uppercase ${
                        online ? "text-green-600" : "text-red-500"
                      }`}
                    >
                      {online ? "Online" : "Paused"}
                    </span>
                  </div>
                  <button
                    type="button"
                    aria-label="Toggle kitchen online"
                    onClick={() =>
                      updateStatus({
                        kitchenId: kitchenDocId(selectedKitchen),
                        nextOnline: !online,
                      })
                    }
                    disabled={isUpdatingStatus}
                    className={`relative inline-flex items-center h-8 rounded-full w-14 transition-colors ${
                      online ? "bg-green-500" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block w-6 h-6 transform bg-white rounded-full shadow transition-transform ${
                        online ? "translate-x-7" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
                <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100">
                  <div>
                    <p className="text-xs text-gray-400 font-bold uppercase">
                      Self delivery
                    </p>
                    <p className="text-sm text-gray-600">
                      {(selectedKitchen as any).selfDelivery
                        ? "Enabled"
                        : "Off (platform delivery)"}
                    </p>
                  </div>
                  <button
                    type="button"
                    aria-label="Toggle self delivery"
                    onClick={() =>
                      toggleSelfDelivery(!(selectedKitchen as any).selfDelivery)
                    }
                    disabled={togglingDelivery}
                    className={`relative inline-flex items-center h-8 rounded-full w-14 transition-colors ${
                      (selectedKitchen as any).selfDelivery
                        ? "bg-orange-500"
                        : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`inline-block w-6 h-6 transform bg-white rounded-full shadow transition-transform ${
                        (selectedKitchen as any).selfDelivery
                          ? "translate-x-7"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </Card>
            )}

            {selectedTab === "orders" && (
              <KitchenOrderManager kitchenId={kitchenDocId(selectedKitchen)} />
            )}

            {selectedTab === "menu" && (
              <ManageMenu
                kitchenId={kitchenDocId(selectedKitchen)}
                chefUid={uid}
                kitchenName={kitchenName}
              />
            )}

            {selectedTab === "announce" && (
              <PasteAnnouncement kitchenId={kitchenDocId(selectedKitchen)} />
            )}

            {selectedTab === "specials" && (
              <ManageSpecials
                kitchenId={kitchenDocId(selectedKitchen)}
                kitchenName={kitchenName}
              />
            )}

            {selectedTab === "chefs" && (
              <KitchenChefsPanel kitchen={selectedKitchen} />
            )}

            {selectedTab === "money" && (
              <KitchenMoneyPanel kitchenId={kitchenDocId(selectedKitchen)} />
            )}
          </Suspense>
        </>
      )}
    </div>
  );
};

export default KitchenManagerDashboard;
